import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useDemoForm } from "@/context/DemoFormContext";
import { ASSETS } from "@/i18n";
import { Menu, X, LogIn } from "lucide-react";

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const { openForm } = useDemoForm();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "features", label: t.nav.features },
    { id: "dashboard", label: t.nav.dashboard },
    { id: "pricing", label: t.nav.pricing },
    { id: "faq", label: t.nav.faq },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a href="#hero" data-testid="logo-link" className="flex items-center gap-3">
          <img
            src={ASSETS.logo}
            alt="AURA"
            className="h-11 w-11 rounded-full object-contain bg-white ring-1 ring-black/5"
            style={{ mixBlendMode: "multiply" }}
          />
          <div className="leading-tight">
            <div className="font-serif text-2xl tracking-tight text-[#1A1A1A]">AURA</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#737373]">CRM · 2026</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className="text-sm text-[#4A4A4A] hover:text-[#1A1A1A] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="language-toggle"
            className="hidden sm:flex items-center bg-white/70 backdrop-blur border border-black/5 rounded-full p-1 text-xs"
          >
            <button
              data-testid="lang-it"
              onClick={() => setLang("it")}
              className={`px-3 py-1 rounded-full tracking-wider transition-all ${
                lang === "it" ? "bg-[#1A1A1A] text-white" : "text-[#737373] hover:text-[#1A1A1A]"
              }`}
            >
              IT
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1 rounded-full tracking-wider transition-all ${
                lang === "en" ? "bg-[#1A1A1A] text-white" : "text-[#737373] hover:text-[#1A1A1A]"
              }`}
            >
              EN
            </button>
          </div>
          <a
            href="#cta"
            data-testid="header-cta-button"
            onClick={(e) => {
              e.preventDefault();
              openForm();
            }}
            className="hidden md:inline-flex items-center bg-[#1A1A1A] text-white hover:bg-[#333] transition-colors rounded-full px-5 py-2.5 text-sm tracking-wide cursor-pointer"
          >
            {t.nav.cta}
          </a>
          <Link
            to="/admin/login"
            data-testid="header-login-link"
            className="hidden md:inline-flex items-center gap-1.5 text-sm text-[#1A1A1A] hover:text-[#737373] transition-colors px-2"
          >
            <LogIn size={14} /> Accedi
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-[#1A1A1A]"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-black/5">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="text-[#1A1A1A] text-base"
                data-testid={`mobile-nav-${l.id}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#cta"
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                openForm();
              }}
              className="mt-2 bg-[#1A1A1A] text-white text-center rounded-full px-5 py-3 text-sm cursor-pointer"
              data-testid="mobile-cta-button"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
